import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toPng } from "html-to-image";
import { Download, CalendarDays, Loader2, XCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmt = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "");

const CardFace = ({ card, innerRef }) => (
  <div ref={innerRef} className="relative w-full max-w-[420px] mx-auto rounded-[22px] overflow-hidden text-white shadow-2xl" style={{ background: `linear-gradient(145deg, ${card.color || "#f0b429"} 0%, #0a1518 55%, #050b0d 100%)` }} data-testid="member-card">
    <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent 0 2px, rgba(255,255,255,.35) 2px 3px)" }} />
    <div className="relative p-6 space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-white/70">FEDEVO 2.0 · Urban Media</div>
          <div className="font-display text-3xl leading-none mt-1">Carte membre</div>
        </div>
        <img src="/media/logo-tv.png" alt="" crossOrigin="anonymous" className="w-14 h-14 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      </div>
      <div className="flex gap-5 items-center">
        <div className="bg-white p-2 rounded-xl shrink-0"><QRCodeSVG value={`${window.location.origin}/cours/verif/${card.token}`} size={132} level="M" includeMargin={false} /></div>
        <div className="min-w-0 space-y-1">
          <div className="font-display text-4xl leading-none truncate" data-testid="member-card-name">{card.first_name} {card.last_name}</div>
          <div className="text-sm font-medium" style={{ color: card.color }}>{card.course_name}</div>
          <div className="text-xs text-white/75">{card.day_label} {card.slot?.start}–{card.slot?.end} · {card.slot?.level}</div>
          <div className="text-xs text-white/75">{card.room} · {card.venue_name}</div>
        </div>
      </div>
      <div className="flex items-end justify-between border-t border-white/15 pt-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">{card.season_label}</div>
          <div className="text-xs mt-0.5">Formule {card.plan_label?.toLowerCase()} · valable jusqu'au <b>{fmt(card.paid_until)}</b></div>
        </div>
        <div className="text-right">
          <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold ${card.valid ? "bg-green-400 text-black" : "bg-red-500 text-white"}`} data-testid="member-card-status">{card.valid ? "Payé" : "Expiré"}</div>
          <div className="font-mono text-[10px] text-white/60 mt-1">N° {card.member_no}</div>
        </div>
      </div>
    </div>
  </div>
);

export default function MemberCard() {
  const { token } = useParams();
  const [card, setCard] = useState(null);
  const [error, setError] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    api.get(`/courses/card/${token}`).then(r => setCard(r.data)).catch(err => setError(apiError(err, "Carte introuvable")));
  }, [token]);

  const save = async () => {
    try {
      const url = await toPng(ref.current, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.href = url; a.download = `carte-fedevo-${(card.first_name || "membre").toLowerCase()}.png`; a.click();
    } catch { toast.error("Impossible de générer l'image, fais une capture d'écran"); }
  };

  if (error) return <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="member-card-error"><XCircle className="w-16 h-16 mx-auto text-[color:var(--text-muted)]" /><h1 className="font-display text-4xl mt-6">Carte indisponible</h1><p className="text-[color:var(--text-muted)] mt-3">{error}</p><Link to="/cours" className="mt-8 inline-block border border-[color:var(--border)] hover:border-[color:var(--accent)] px-6 py-3 rounded-full text-xs uppercase tracking-widest">Retour aux cours</Link></div>;
  if (!card) return <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="member-card-loading"><Loader2 className="w-12 h-12 mx-auto animate-spin text-[color:var(--accent)]" /></div>;

  return (
    <div className="max-w-2xl mx-auto px-6 py-14 space-y-8" data-testid="member-card-page">
      <div className="text-center">
        <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--accent)] inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Carte membre numérique</div>
        <h1 className="font-display text-5xl sm:text-6xl mt-3 leading-none">Bienvenue, {card.first_name}.</h1>
        <p className="text-sm text-[color:var(--text-muted)] mt-3">Présente ce QR code à l'entrée du cours : la régie le scanne pour vérifier ton inscription.</p>
      </div>
      <CardFace card={card} innerRef={ref} />
      <div className="flex items-center justify-center gap-3 flex-wrap">
        <button type="button" onClick={save} data-testid="member-card-save" className="inline-flex items-center gap-2 bg-[color:var(--accent)] text-black font-semibold px-6 py-3 rounded-full text-xs uppercase tracking-widest hover:brightness-110 transition"><Download className="w-4 h-4" /> Enregistrer l'image</button>
        <a href={`${BACKEND}/api/courses/agenda/${token}.ics`} data-testid="member-card-agenda" className="inline-flex items-center gap-2 border border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] px-6 py-3 rounded-full text-xs uppercase tracking-widest transition-colors"><CalendarDays className="w-4 h-4" /> Mon agenda (.ics)</a>
      </div>
      <p className="text-[10px] text-center text-[color:var(--text-muted)]">Lien personnel : ne le partage pas. Astuce : ajoute cette page à l'écran d'accueil de ton téléphone.</p>
    </div>
  );
}
